import React, { useMemo, useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
    Checkbox,
    Typography
} from "@mui/material";
import { columns } from "../../../dtos/columns";

interface TablaSelectorProps<T> {
    titulo?: React.ReactNode;
    columns: columns<T>[];
    data: T[];
    esMultiple?: boolean;
    setFilaSeleccionada: React.Dispatch<React.SetStateAction<any>>;
    filasSeleccionadas?: T[];
    conditionalRowStyles?: any;
    messageSingular?: string;
    messagePlural?: string;
}

const TablaSelector = <T,>({
    titulo,
    columns,
    data,
    esMultiple = false,
    setFilaSeleccionada,
    filasSeleccionadas = [],
    conditionalRowStyles,
    messageSingular = "registro",
    messagePlural = "registros"
}: TablaSelectorProps<T>) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState<"asc" | "desc">("asc");
    const [orderBy, setOrderBy] = useState<keyof T | null>(null);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const getRowId = (row: any) => {
        return row.id || JSON.stringify(row);
    };

    // Sincronizar selectedRows con filasSeleccionadas del componente padre
    useEffect(() => {
        const selectedIds = new Set(filasSeleccionadas.map(row => getRowId(row)));
        setSelectedRows(selectedIds);
    }, [filasSeleccionadas]);

    const handleSort = (property: keyof T) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Ordenar los datos en memoria
    const sortedData = useMemo(() => {
        if (!orderBy) return data;
        return [...data].sort((a, b) => {
            const valueA = a[orderBy] as any;
            const valueB = b[orderBy] as any;
            return (valueA < valueB ? -1 : 1) * (order === "asc" ? 1 : -1);
        });
    }, [data, orderBy, order]);

    // Selección de filas
    const toggleRowSelection = (row: any) => {
        const rowId = getRowId(row);
        if (esMultiple) {
            setSelectedRows(prev => {
                const newSet = new Set(prev);
                if (newSet.has(rowId)) {
                    newSet.delete(rowId);
                } else {
                    newSet.add(rowId);
                }
                const selectedData = sortedData.filter(r => newSet.has(getRowId(r)));
                setTimeout(() => setFilaSeleccionada(selectedData), 0);
                return newSet;
            });
        } else {
            setSelectedRows(new Set([rowId]));
            setTimeout(() => setFilaSeleccionada(row), 0);
        }
    };

    const toggleSelectAll = () => {
        if (esMultiple) {
            if (selectedRows.size === sortedData.length) {
                setSelectedRows(new Set());
                setFilaSeleccionada([]);
            } else {
                const allIds = sortedData.map(r => getRowId(r));
                setSelectedRows(new Set(allIds));
                setFilaSeleccionada([...sortedData]);
            }
        }
    };

    const isAllSelected = sortedData.length > 0 && selectedRows.size === sortedData.length;
    const isSomeSelected = selectedRows.size > 0 && selectedRows.size < sortedData.length;

    return (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
            {titulo}
            <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                <Table sx={{ tableLayout: "auto", width: "100%" }}>
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell padding="checkbox">
                                {esMultiple && (
                                    <Checkbox
                                        indeterminate={isSomeSelected}
                                        checked={isAllSelected}
                                        onChange={toggleSelectAll}
                                    />
                                )}
                            </TableCell>
                            {columns.map((column) =>
                                !column.omit ? (
                                    <TableCell
                                        key={column.name as string}
                                        align={column.alling || "left"}
                                        width={column.width || "auto"}
                                        sx={{ minWidth: column.width || 120, paddingX: 2, paddingY: 2 }}
                                    >
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === column.sortField}
                                                direction={orderBy === column.sortField ? order : "asc"}
                                                onClick={() => handleSort(column.sortField as keyof T)}
                                            >
                                                {column.name}
                                            </TableSortLabel>
                                        ) : (
                                            column.name
                                        )}
                                    </TableCell>
                                ) : null
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} align="center">
                                    No se ha encontrado data
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                                <TableRow
                                    key={getRowId(row)}
                                    hover
                                    selected={selectedRows.has(getRowId(row))}
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => toggleRowSelection(row)}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedRows.has(getRowId(row))}
                                            onChange={() => toggleRowSelection(row)}
                                            onClick={() => toggleRowSelection(row)}
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </TableCell>
                                    {columns.map((column) =>
                                        !column.omit ? (
                                            <TableCell
                                                key={column.name as string}
                                                align={column.alling || "left"}
                                                sx={{
                                                    whiteSpace: column.wrap ? "normal" : "nowrap",
                                                    minWidth: column.width || 120,
                                                    paddingX: 2,
                                                    paddingY: 2,
                                                }}
                                            >
                                                {column.cell ? column.cell(row) : column.selector(row)}
                                            </TableCell>
                                        ) : null
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: "Todos", value: data.length }]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage === -1 ? data.length : rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) =>
                    `${from}-${to} de ${count}`
                } slotProps={{
                    displayedRows: {
                        sx: { mt: 2 }
                    }
                }}
            />
        </Paper>
    );
};

export default TablaSelector; 